import React, { Dispatch } from "react";
import { RouteComponentProps, Link } from "react-router-dom";
import { connect } from "react-redux";
import { showSnack } from "react-redux-snackbar";
import { UserState } from "redux-oidc";
import { FeedbackDetails } from "./FeedbackDetails";
import {
  IAppState,
  ErrorDisplay,
  LoadingDisplay,
  ApiRequest,
  CommentsDisabledDisplay,
  appPaths
} from "../../../Common";
import {
  FeedbackCategory,
  FeedbackState,
  FeedbackVote,
  FeedbackParent
} from "../../Constants/feedbackEnums";
import {
  getFeedbackDetails,
  resetLoading,
  setLoading,
  updateLoadedFeedback
} from "../../Actions/FeedbackDetailsActions";
import {
  Feedback,
  FeatureRequest,
  BugReport,
  ResourceRequest,
  RelationshipDisplayFeedback
} from "../../Models/Feedback";
import { BugReportDetails } from "./BugReportDetails";
import { ResourceRequestDetails } from "./ResourceRequestDetails";
import { FeatureRequestDetails } from "./FeatureRequestDetails";
import { feedbackVoteService } from "../../Services/FeedbackVoteService";
import CommentBox from "../Common/CommentBox/CommentBox";
import { endpoints } from "../..";
import ResponseHandler from "../../../App/ResponseHandler";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import { Application } from "../../../App";
import { setActiveApp, setActiveTopic, setPath } from "../../../Startup/Actions/StartupActions";
import { Topic } from "../../../Topic/Models/Topic";
import { User } from "../../../User";
import { feedbackService } from "../../Services/FeedbackService";

type TParams = {
  code: string;
};

type StateProps = {
  isLoading: boolean;
  contentIsLoaded: boolean;
  feedback: FeatureRequest | BugReport | ResourceRequest | null;
  hasError: boolean;
  errorCode: number | null;
  errorDescription: string;
  activeApp?: Application;
  activeTopic?: Topic;
  parentType: FeedbackParent;
  user: UserState;
  path?: string;
};

type DispatchProps = {
  getFeedbackDetails: (url: string) => void;
  resetLoading: () => void;
  setLoading: () => void;
  updateLoadedFeedback: (updatedFeedback: FeatureRequest | BugReport | ResourceRequest) => void;
  showSnack: (uniqueId: any, args: any) => void;
  setActiveApp: (app: Application) => void;
  setActiveTopic: (topic: Topic) => void;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  isLoading: state.feedbackDetails.isLoading,
  contentIsLoaded: state.feedbackDetails.contentIsLoaded,
  feedback: state.feedbackDetails.feedback,
  hasError: state.feedbackDetails.hasError,
  errorCode: state.feedbackDetails.errorCode,
  errorDescription: state.feedbackDetails.errorDescription,
  activeApp: state.app.activeApp,
  parentType: state.app.parentType,
  user: state.oidc
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  getFeedbackDetails: (code: string) => dispatch(getFeedbackDetails(code)),
  resetLoading: () => dispatch(resetLoading()),
  setLoading: () => dispatch(setLoading()),
  updateLoadedFeedback: (updatedFeedback: FeatureRequest | BugReport | ResourceRequest) =>
    dispatch(updateLoadedFeedback(updatedFeedback)),
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args)),
  setActiveApp: (app: Application) => dispatch(setActiveApp(app)),
  setActiveTopic: (topic: Topic) => dispatch(setActiveTopic(topic))
});

class ViewFeedbackContainer extends React.Component<
  StateProps & DispatchProps & RouteComponentProps<TParams>
> {
  constructor(props: StateProps & DispatchProps & RouteComponentProps<TParams>) {
    super(props);
    this.onFeedbackStateChange = this.onFeedbackStateChange.bind(this);
    this.onVoteFeedback = this.onVoteFeedback.bind(this);
  }

  componentDidUpdate = (prevProps: StateProps & DispatchProps & RouteComponentProps<TParams>) => {
    if (this.props.match.params.code !== prevProps.match.params.code) {
      this.props.setLoading();
      this.props.getFeedbackDetails(this.props.match.params.code);
    }

    if (this.props.contentIsLoaded && !this.props.isLoading && !!this.props.feedback) {
      if (
        this.props.feedback.parent === FeedbackParent.Application &&
        !!this.props.activeApp &&
        this.props.activeApp.rolePrefix !== this.props.feedback.application.rolePrefix
      ) {
        if (this.props.location.pathname.includes("/featurerequests/")) {
          this.props.history.push(
            appPaths.app.featureRequests.replace(":id", this.props.activeApp.rolePrefix)
          );
        }
        if (this.props.location.pathname.includes("/bugreports/")) {
          this.props.history.push(
            appPaths.app.bugReports.replace(":id", this.props.activeApp.rolePrefix)
          );
        }
        if (this.props.location.pathname.includes("/resourcerequests/")) {
          this.props.history.push(
            appPaths.app.resourceRequests.replace(":id", this.props.activeApp.rolePrefix)
          );
        }
      } else if (
        this.props.feedback.parent === FeedbackParent.Topic &&
        this.props.activeTopic &&
        this.props.activeTopic.code !== this.props.feedback.topic.code
      ) {
        // TO DO: clean up logic to ensure user is kicked out of topic if they provide a topic code that the feedback does not belong to
        this.props.history.push(appPaths.topic.root);
      }
    }
  };

  componentDidMount = () => {
    if (!this.props.isLoading) {
      if (!this.props.contentIsLoaded) {
        this.props.setLoading();
        this.props.getFeedbackDetails(this.props.match.params.code);
      } else {
        const feedbackCode = (this.props.feedback as Feedback).code;
        const paramsCode = this.props.match.params.code;

        if (feedbackCode !== paramsCode) {
          this.props.setLoading();
          this.props.getFeedbackDetails(this.props.match.params.code);
        }
      }
    }
  };

  onVoteFeedback(feedbackCode: string, vote: FeedbackVote) {
    // get this feedback from the props
    const thisFeedback = this.props.feedback;

    if (thisFeedback) {
      // update vote count
      thisFeedback.voteCount = feedbackVoteService.getNewCountAfterVote(thisFeedback, vote);

      // update logged in user's vote
      thisFeedback.loggedInUserVote = vote.toString();

      // dispatch an action
      this.props.updateLoadedFeedback(thisFeedback);
    }
  }

  getCategory = (feedback: any) => {
    if (
      (feedback as FeatureRequest).category === FeedbackCategory[FeedbackCategory.FeatureRequest]
    ) {
      return FeedbackCategory.FeatureRequest;
    }
    if ((feedback as BugReport).category === FeedbackCategory[FeedbackCategory.BugReport]) {
      return FeedbackCategory.BugReport;
    }

    if (
      (feedback as ResourceRequest).category === FeedbackCategory[FeedbackCategory.ResourceRequest]
    ) {
      return FeedbackCategory.ResourceRequest;
    }

    throw new Error("Feedback category not found");
  };

  onFeedbackStateChange = (feedbackCode: string) => {
    // get this feedback from the server
    ApiRequest.fromPath(endpoints.feedback.getByCode(feedbackCode))
      .withAuthHeader()
      .get()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult) => {
            this.props.updateLoadedFeedback(result.payload);
          },
          (result: ResponseResult) => {
            this.showFeedbackStateError(response.status, result.error.message);
          },
          () => {
            this.showFeedbackStateError(response.status, "An unknown error has occured");
          }
        );
      });
  };

  onAssignedUsersChange = (users: User[]) => {
    // get this feedback from the props
    const thisFeedback = this.props.feedback;

    if (thisFeedback) {
      // update assigned to
      thisFeedback.assignedTo = users.map(u => ({
        id: u.id,
        username: u.username,
        name: u.name
      }));

      // dispatch an action
      this.props.updateLoadedFeedback(thisFeedback);
    }
  };

  onDueDateChange = (dueDate: Date | undefined) => {
    // get this feedback from the props
    const thisFeedback = this.props.feedback;

    if (thisFeedback) {
      // update assigned to
      thisFeedback.dueDate = dueDate ? dueDate.toISOString() : undefined;

      // dispatch an action
      this.props.updateLoadedFeedback(thisFeedback);
    }
  };

  onRelationshipChange = () => {
    this.props.getFeedbackDetails(this.props.match.params.code);
  };

  onMoveFeedback = (feedback: Feedback) => {
    this.props.setLoading();
    this.props.getFeedbackDetails(this.props.match.params.code);
    this.props.history.push(feedbackService.getViewUrl(feedback));
  };

  showFeedbackStateError(errorCode: number, errorDescription: string) {
    this.props.showSnack("FEEDBACK_DETAILS_ERROR_SNACK", {
      label: `Failed to change Feedback State: ${errorDescription} (Error Code ${errorCode})`,
      timeout: 5000
    });
  }

  render() {
    if (this.props.hasError) {
      return (
        <ErrorDisplay
          errorCode={this.props.errorCode as number}
          errorDescription={this.props.errorDescription}
          errorResult="Failed to load feedback"
        />
      );
    }

    if (this.props.contentIsLoaded && !this.props.isLoading && !!this.props.feedback) {
      const category: FeedbackCategory = this.getCategory(this.props.feedback);
      const shouldAutocompleteForOtherCompanyUsers =
        this.props.feedback.parent === FeedbackParent.Application;

      if (!this.props.feedback.permissions) {
        throw Error("Permissions must be loaded for this page");
      }

      switch (category) {
        case FeedbackCategory.FeatureRequest: {
          const featureRequest: FeatureRequest = this.props.feedback as FeatureRequest;

          return (
            <FeedbackDetails
              feedback={this.props.feedback as Feedback}
              user={this.props.user}
              onVote={this.onVoteFeedback}
              onStateChanged={this.onFeedbackStateChange}
              onMoveFeedback={this.onMoveFeedback}
              onAssignedUsersChange={this.onAssignedUsersChange}
              onDueDateChange={this.onDueDateChange}
              onRelationshipChange={this.onRelationshipChange}
              activeApp={this.props.activeApp as Application}
              activeTopic={this.props.activeTopic as Topic}
              paramsCode={this.props.match.params.code}
            >
              <FeatureRequestDetails feedback={featureRequest} user={this.props.user} />
              <hr />
              {this.props.feedback.areCommentsEnabled && (
                <CommentBox
                  code={this.props.match.params.code}
                  shouldAutocompleteForOtherCompanyUsers={shouldAutocompleteForOtherCompanyUsers}
                />
              )}
              {!this.props.feedback.areCommentsEnabled && <CommentsDisabledDisplay />}
            </FeedbackDetails>
          );
        }
        case FeedbackCategory.BugReport: {
          const bugReport: BugReport = this.props.feedback as BugReport;

          return (
            <FeedbackDetails
              feedback={this.props.feedback as Feedback}
              user={this.props.user}
              onVote={this.onVoteFeedback}
              onStateChanged={this.onFeedbackStateChange}
              onMoveFeedback={this.onMoveFeedback}
              onAssignedUsersChange={this.onAssignedUsersChange}
              onDueDateChange={this.onDueDateChange}
              onRelationshipChange={this.onRelationshipChange}
              activeApp={this.props.activeApp as Application}
              activeTopic={this.props.activeTopic as Topic}
              paramsCode={this.props.match.params.code}
            >
              <BugReportDetails feedback={bugReport} user={this.props.user} />
              <CommentBox
                code={this.props.match.params.code}
                shouldAutocompleteForOtherCompanyUsers={shouldAutocompleteForOtherCompanyUsers}
              />
            </FeedbackDetails>
          );
        }
        case FeedbackCategory.ResourceRequest: {
          const resourceRequest: ResourceRequest = this.props.feedback as ResourceRequest;

          return (
            <FeedbackDetails
              feedback={this.props.feedback as Feedback}
              user={this.props.user}
              onVote={this.onVoteFeedback}
              onStateChanged={this.onFeedbackStateChange}
              onMoveFeedback={this.onMoveFeedback}
              onAssignedUsersChange={this.onAssignedUsersChange}
              onDueDateChange={this.onDueDateChange}
              onRelationshipChange={this.onRelationshipChange}
              activeApp={this.props.activeApp as Application}
              activeTopic={this.props.activeTopic as Topic}
              paramsCode={this.props.match.params.code}
            >
              <ResourceRequestDetails feedback={resourceRequest} user={this.props.user} />
              <CommentBox
                code={this.props.match.params.code}
                shouldAutocompleteForOtherCompanyUsers={shouldAutocompleteForOtherCompanyUsers}
              />
            </FeedbackDetails>
          );
        }
        default:
          throw new Error("Invalid feedback category");
      }
    } else {
      return <LoadingDisplay />;
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ViewFeedbackContainer);
